/**
 * Sign in, sign up, and what the account is licensed for.
 *
 * The screen mirrors the desktop one on purpose: the same fields, the same order,
 * the same words for the same failures. A person who signed in on one and is asked
 * to sign in on the other should not have to learn a second set of names.
 *
 * Every sentence is read from the dictionary, and the expiry date is formatted for
 * the language on screen rather than always in `pt-BR`. A Brazilian reader and a
 * Spanish reader get the same date written the way their own country writes it.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, Download, LogOut, Loader2, ShieldCheck } from 'lucide-react';

import { ApiError, messageOf, type Order, fetchOrder } from '../lib/api';
import { releaseVersionFor, useReleases } from '../lib/useReleases';
import { appNameFor, hasApp, useAuth } from '../auth/AuthProvider';
import { appsFor, type AppFacts } from '../content/apps';
import { format, intlLocale, RichText, useI18n } from '../i18n';
import { Link } from '../router';

type Mode = 'sign-in' | 'sign-up';

/** The four states a form can be in, so none of them is implied by a missing flag. */
type FormState = 'idle' | 'submitting' | 'done' | 'error';

export const AccountView: React.FC = () => {
  const auth = useAuth();
  const { t } = useI18n();

  if (auth.status === 'loading') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-gold" aria-hidden="true" />
        <span className="sr-only">{t.account.loadingAccount}</span>
      </div>
    );
  }

  return auth.session === null ? <SignInView /> : <SignedInView />;
};

// ---------------------------------------------------------------------------
// Signed out
// ---------------------------------------------------------------------------

const SignInView: React.FC = () => {
  const { signIn, signUp, signInWithOAuth, error, needsConfirmation, clearError } = useAuth();
  const { t } = useI18n();

  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [state, setState] = useState<FormState>('idle');
  // Announced to assistive technology on submit, because the message appears below
  // the fold on a short window and a screen reader would otherwise pass over it.
  const messageRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (error !== null) messageRef.current?.focus();
  }, [error, needsConfirmation]);

  const busy = state === 'submitting';

  /*
    Resolved here, at render, rather than stored as a sentence. The alternative shows
    an error raised in one language under a heading in another, which is the one place
    on the site where a reader is most likely to switch language mid-flow: they cannot
    read the message, so they change it.
  */
  const errorText = error === null ? null : messageOf(error, t.account.signInBody);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    clearError();
    setState('submitting');

    try {
      if (mode === 'sign-in') await signIn(email, password);
      else await signUp(email, password);
      setState('done');
    } catch (caught) {
      // The message itself is already in `error`, set by the provider. This only
      // has to stop the form pretending it succeeded.
      setState(caught instanceof ApiError && caught.code === 'already_registered' ? 'done' : 'error');
    }
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    clearError();
    setState('idle');
  };

  return (
    <section className="mx-auto w-full max-w-md px-5 py-16 sm:py-24">
      <header className="mb-8 text-center">
        <img src="/OrobasWorkLogoNewGold.svg" alt={t.account.crestAlt} width={40} height={40} className="block mx-auto mb-6" />
        <h1 className="text-[clamp(1.7rem,4vw,2.2rem)] font-semibold leading-tight tracking-[-0.025em] text-cream">
          {mode === 'sign-in' ? t.account.signInTitle : t.account.signUpTitle}
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-[15px] leading-[1.5] text-muted">
          {mode === 'sign-in' ? t.account.signInBody : t.account.signUpBody}
        </p>
      </header>

      {needsConfirmation ? (
        /*
          The password comes from this component's state, which still holds the one
          the account was created with. See ConfirmationNotice for why the button
          could not work without it.
        */
        <ConfirmationNotice email={email} password={password} onSignIn={signIn} />
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Field
            id="email"
            label={t.account.emailLabel}
            type="email"
            autoComplete="email"
            value={email}
            onChange={setEmail}
            disabled={busy}
            required
          />
          <Field
            id="password"
            label={t.account.passwordLabel}
            type="password"
            // `current-password` for signing in and `new-password` for signing up
            // are different hints, and a password manager will offer the wrong one
            // if both say the same thing.
            autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
            value={password}
            onChange={setPassword}
            disabled={busy}
            minLength={6}
            required
          />

          {/*
            focusable so a screen reader lands here after a failed submit, and
            aria-live so the message is announced without stealing the caret.
          */}
          <p
            ref={messageRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            className="min-h-[1.25rem] text-sm text-danger focus:outline-none"
          >
            {errorText ?? ''}
          </p>

          <button
            type="submit"
            disabled={busy}
            className="pill pressable w-full px-5 py-3.5 text-[15px] text-ink disabled:cursor-not-allowed disabled:opacity-60 bg-gold hover:bg-gold-bright"
          >
            {busy ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                {mode === 'sign-in' ? t.account.signingIn : t.account.signingUp}
              </>
            ) : (
              <>
                {mode === 'sign-in' ? t.account.signInAction : t.account.signUpAction}
                <ArrowRight className="size-4" aria-hidden="true" />
              </>
            )}
          </button>

          {mode === 'sign-in' && (
            <>
              <p className="text-center text-sm text-muted">{t.account.orDivider}</p>

              <button
                type="button"
                disabled={busy}
                onClick={() => void signInWithOAuth('google')}
                className="pill pressable w-full border border-line-strong px-5 py-3 text-[14px] text-cream hover:bg-white/8 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t.account.signInWithGoogle}
              </button>

              <button
                type="button"
                disabled={busy}
                onClick={() => void signInWithOAuth('github')}
                className="pill pressable w-full border border-line-strong px-5 py-3 text-[14px] text-cream hover:bg-white/8 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t.account.signInWithGitHub}
              </button>
            </>
          )}

          <p className="text-center text-sm text-muted">
            {mode === 'sign-in' ? t.account.noAccountYet : t.account.hasAccount}{' '}
            <button
              type="button"
              onClick={() => switchMode(mode === 'sign-in' ? 'sign-up' : 'sign-in')}
              className="font-medium text-gold underline-offset-4 hover:underline"
            >
              {mode === 'sign-in' ? t.account.createOne : t.account.signInAction}
            </button>
          </p>
        </form>
      )}
    </section>
  );
};

/**
 * The "check your e-mail" state.
 *
 * Its own screen rather than an error line, because nothing went wrong: the account
 * was created and the server is waiting for a confirmation. It has its own action
 * (go back and sign in), which is why it is a state and not a message.
 */
const ConfirmationNotice: React.FC<{
  email: string;
  /**
   * The password the account was just created with, handed down from the form.
   *
   * This used to call `signIn(email, '')` on the theory that an empty password is
   * "just a check". GoTrue does not have such a request: an empty password is a wrong
   * password, so the button could only ever report failure, and it reported it as
   * "ainda não confirmou?", which is a lie told to somebody who *had* confirmed and
   * was then asked to check their inbox again.
   *
   * Passing the form's own value is not a second copy of anything: the form is the
   * only place the password has ever been, this is the same string handed one level
   * down, and it is dropped the moment the screen goes away.
   */
  password: string;
  onSignIn: (email: string, password: string) => Promise<void>;
}> = ({ email, password, onSignIn }) => {
  const { clearError } = useAuth();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const retry = async () => {
    if (busy) return;
    clearError();
    setBusy(true);
    setFailed(false);
    try {
      await onSignIn(email, password);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 rounded-glass glass p-7 text-center">
      <div className="mx-auto flex size-11 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
        <ShieldCheck className="size-5 text-gold" aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-cream">{t.account.confirmTitle}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          <RichText text={t.account.confirmBody} values={{ email }} />
        </p>
      </div>
      {/*
        A real sign-in with the password this account was created with. It succeeds
        exactly when the confirmation has landed, which is the only thing the reader
        is asking it to find out.
      */}
      <button
        type="button"
        onClick={() => void retry()}
        disabled={busy}
        className="pill pressable w-full border border-line-strong px-5 py-2.5 text-[15px] font-medium text-cream hover:bg-white/8 disabled:opacity-60"
      >
        {busy ? t.account.checking : t.account.confirmedSignIn}
      </button>

      {failed && (
        <p className="text-sm text-muted">
          {t.account.wrongPassword}{' '}
          <button
            type="button"
            onClick={clearError}
            className="font-medium text-gold underline-offset-4 hover:underline"
          >
            {t.account.backToSignIn}
          </button>
        </p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Signed in
// ---------------------------------------------------------------------------

const SignedInView: React.FC = () => {
  const { account, entitlements, signOut, refreshing, refreshEntitlements } = useAuth();
  const { locale, t } = useI18n();

  // Resolved once and shared by the two panels below. Two calls built the identical
  // array twice per render for no benefit.
  const licensed = useMemo(
    () => appsFor(locale).filter((app) => hasApp(entitlements, app.id)),
    [locale, entitlements],
  );

  return (
    <section className="mx-auto w-full max-w-3xl px-5 py-16 sm:px-8 sm:py-20">
      <header className="mb-10 flex flex-wrap items-start justify-between gap-4 border-b border-line pb-8">
        <div className="min-w-0">
          <p className="text-sm text-muted">{t.account.accountLabel}</p>
          <h1 className="mt-1 truncate text-[clamp(1.5rem,3.4vw,2rem)] font-semibold tracking-[-0.025em] text-cream">
            {account?.email ?? t.account.yourAccount}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="pill pressable shrink-0 border border-line-strong px-4 py-2 text-sm text-muted hover:bg-white/8 hover:text-cream"
        >
          <LogOut className="size-4" aria-hidden="true" />
          {t.account.signOut}
        </button>
      </header>

      <Entitlements
        licensed={licensed}
        entitlements={entitlements}
        refreshing={refreshing}
        onRefresh={refreshEntitlements}
      />

      <Downloads unlocked={licensed} />
    </section>
  );
};

const Entitlements: React.FC<{
  /**
   * Already filtered by the parent, which is also what it is paired against when it
   * reads each grant: a grant is found by `appId`, so the two lists have to be the
   * same list rather than two filters of the same array.
   */
  licensed: readonly AppFacts[];
  entitlements: ReturnType<typeof useAuth>['entitlements'];
  refreshing: boolean;
  onRefresh: () => Promise<void>;
}> = ({ licensed, entitlements, refreshing, onRefresh }) => {
  const { locale, t } = useI18n();

  return (
    <div className="mb-12">
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold text-cream">{t.account.licensesTitle}</h2>
        <button
          type="button"
          onClick={() => void onRefresh()}
          disabled={refreshing}
          className="text-sm text-muted transition-colors hover:text-cream disabled:opacity-50"
        >
          {refreshing ? t.account.refreshing : t.account.refresh}
        </button>
      </div>

      {licensed.length === 0 ? (
        // An empty list is a designed state with an action in it, not a blank
        // panel: the reader arrived here because they are signed in and not
        // licensed, so the next thing to do has to be on this screen.
        <div className="glass rounded-glass p-7">
          <p className="text-sm leading-relaxed text-muted">{t.account.noLicensesBody}</p>
          <Link
            to="planos"
            className="pill pressable mt-6 bg-gold px-5 py-2.5 text-sm text-ink hover:bg-gold-bright"
          >
            {t.account.viewPlans}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {licensed.map((app) => {
            const grant = entitlements.find((e) => e.appId === app.id);
            return (
              <li key={app.id} className="glass rounded-input border-gold/30 p-5">
                <div className="flex items-center gap-2">
                  <Check className="size-4 text-gold" aria-hidden="true" />
                  <span className="font-medium text-cream">{appNameFor(app.id)}</span>
                </div>
                <p className="mt-1.5 pl-6 text-xs text-muted">
                  {grant?.plan === 'pro' ? t.account.lifetimeLicense : grant?.plan ?? t.account.licensedFallback}
                  {grant?.expiresAt != null
                    ? ` - ${format(t.account.validUntil, {
                        date: new Date(grant.expiresAt).toLocaleDateString(intlLocale(locale)),
                      })}`
                    : ''}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/**
 * The installers.
 *
 * Present only for an app this account is licensed for. Hiding them rather than
 * greying them out is deliberate: a disabled download button invites the reader to
 * wonder what would make it work, and the answer is on the page above.
 */
const Downloads: React.FC<{ unlocked: readonly AppFacts[] }> = ({ unlocked }) => {
  const { t } = useI18n();
  // The version of each download, as the release table says it is. Held even when
  // the list is empty of licensed apps, though the early return below means the
  // fetch only runs while there is something to download.
  const releases = useReleases();
  if (unlocked.length === 0) return null;

  return (
    <div className="mb-12">
      <h2 className="mb-4 text-lg font-semibold text-cream">{t.account.installersTitle}</h2>
      <ul className="space-y-3">
        {unlocked.map((app) => {
          const version = releaseVersionFor(releases, app.id);
          return (
            <li key={app.id} className="glass flex flex-wrap items-center justify-between gap-4 rounded-input p-5">
              <div className="min-w-0">
                <p className="font-medium text-cream">{app.name}</p>
                <p className="mt-0.5 text-xs text-muted">
                  {app.requirements}
                </p>
                {version !== undefined && (
                  <p className="mt-0.5 text-xs text-muted">
                    {format(t.account.versionLabel, { version })}
                  </p>
                )}
              </div>
              {/*
                A zip, not a dmg. These builds are signed ad-hoc and are not
                notarised, and Gatekeeper refuses an unnotarised dmg on first open
                where it only warns on an unnotarised zip, so the zip is the difference
                between "you have to right-click Open once" and "this file is from an
                unidentified developer, it will be deleted". The extraction step is one
                line in the notice below the list.
              */}
              <a
                href={`/downloads/${app.id}.zip`}
                download
                className="pill pressable border border-gold/50 px-4 py-2.5 text-sm font-medium text-gold hover:bg-gold/12"
              >
                <Download className="size-4" aria-hidden="true" />
                {t.account.download}
              </a>
            </li>
          );
        })}
      </ul>

      {/*
        The Gatekeeper sentence, before the reader hits it rather than after. Both
        builds are signed ad-hoc and unnotarised, so the first open needs a
        right-click; saying so here is the difference between an instruction and a
        support ticket.
      */}
      <p className="mt-4 text-xs leading-relaxed text-muted">{t.account.gatekeeperNote}</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The post-payment landing page
// ---------------------------------------------------------------------------

/**
 * Where Mercado Pago sends the buyer back to.
 *
 * It polls the order rather than trusting the URL: the provider can send someone
 * here with `status=approved` in the query string without any money having moved,
 * and this page grants nothing on its own. The order is the only thing that
 * decides, and it is read from the API.
 */
export const OrderStatusView: React.FC<{ orderId: string }> = ({ orderId }) => {
  const { session, refreshEntitlements } = useAuth();
  const { t } = useI18n();
  const [order, setOrder] = useState<Order | null>(null);
  // Held as thrown, resolved at render, so the heading and the sentence under it are
  // always in the same language.
  const [failure, setFailure] = useState<unknown>(null);

  useEffect(() => {
    if (session === null) return;
    let cancelled = false;
    let timer = 0;

    // An order only reaches one of these states once and never leaves it, so the
    // poller stops as soon as one arrives. Without this the interval ran for the
    // full two minutes after a successful payment, about forty `fetchOrder` calls
    // plus forty `fetchAccount` calls, because every tick on an approved order also
    // refreshed the entitlements.
    const isFinal = (status: Order['status']) =>
      status === 'approved' || status === 'rejected' || status === 'refunded' || status === 'expired';

    const poll = async () => {
      try {
        const { order: current } = await fetchOrder(session.accessToken, orderId);
        if (cancelled) return;
        setOrder(current);
        if (isFinal(current.status)) window.clearInterval(timer);
        if (current.status === 'approved') {
          // The licence is live; make the header and the pricing cards agree with it
          // without a reload.
          await refreshEntitlements();
        }
      } catch (caught) {
        if (!cancelled) setFailure(caught);
      }
    };

    void poll();

    timer = window.setInterval(() => {
      void poll();
    }, 3000);
    const stop = window.setTimeout(() => window.clearInterval(timer), 120_000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
    // A language change must not restart a poll that is already running: the order
    // is the same order. The dictionary is read through `activeDictionary` by
    // `api.ts`, so a message thrown after a switch is already in the new language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, orderId, refreshEntitlements]);

  const heading = (() => {
    if (failure !== null) {
      return { title: t.account.order.readFailedTitle, body: messageOf(failure, t.account.order.readFailedTitle) };
    }
    // Signed out there is no order to poll, so "checking" would never resolve into
    // anything else. A buyer who paid in one browser and opened the return link in
    // another lands here, and the page has to say why it is showing nothing.
    if (session === null) {
      return {
        title: t.account.order.signInRequiredTitle,
        body: t.account.order.signInRequiredBody,
      };
    }
    if (order === null) {
      return { title: t.account.order.checkingTitle, body: t.account.order.checkingBody };
    }
    if (order.status === 'approved') {
      return { title: t.account.order.approvedTitle, body: t.account.order.approvedBody };
    }
    if (order.status === 'rejected' || order.status === 'refunded') {
      return { title: t.account.order.incompleteTitle, body: t.account.order.incompleteBody };
    }
    if (order.status === 'expired') {
      return { title: t.account.order.expiredTitle, body: t.account.order.expiredBody };
    }
    return { title: t.account.order.pendingTitle, body: t.account.order.pendingBody };
  })();

  const tone =
    order?.status === 'approved'
      ? 'text-gold'
      : order?.status === 'rejected' || order?.status === 'refunded' || order?.status === 'expired'
        ? 'text-danger'
        : 'text-cream';

  return (
    <section className="mx-auto w-full max-w-lg px-5 py-20 text-center">
      <h1 className={`text-2xl font-semibold tracking-tight ${tone}`}>{heading.title}</h1>
      <p className="mt-3 text-sm leading-relaxed text-muted">{heading.body}</p>
      <Link
        to="conta"
        className="pill pressable mt-8 bg-gold px-6 py-3.5 text-[15px] text-ink hover:bg-gold-bright"
      >
        {t.account.order.goToAccount}
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </section>
  );
};

// ---------------------------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  autoComplete?: string;
  minLength?: number;
  required: boolean;
}

/** A labelled input, with the label tied by id rather than by placeholder. */
const Field: React.FC<FieldProps> = ({ id, label, type, value, onChange, disabled, autoComplete, minLength, required }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-[13px] font-medium text-cream">
      {label}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      autoComplete={autoComplete}
      minLength={minLength}
      required={required}
      className="w-full rounded-input border border-line-strong bg-white/5 px-4 py-3 text-[15px] text-cream transition-colors placeholder:text-muted focus:border-gold focus:bg-white/8"
    />
  </div>
);